import React, { useRef, useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./Gallery.css";

import pmusa from "../assets/Pmusa/Pmusa.jpg";
import serio from "../assets/Serio/Serio.jpg";
import reis from "../assets/Reis/Reis.jpg";
import ma1 from "../assets/oou/w1.jpg";
import ea from "../assets/easy/2.jpg";
import ofi1 from "../assets/covers/sha/ofi/5.jpg";

const images = [
  { src: pmusa, title: "Pmusa Brand" },
  { src: ma1, title: "Ousi" },
  { src: reis, title: "Reis Visual" },
  { src: serio, title: "Serio Identity" },
  { src: ea, title: "Easy" },
  { src: ofi1, title: "Oficial" },
];

export default function PremiumGallery() {
  const trackRef = useRef(null);

  const velocity = useRef(0);
  const raf = useRef(null);

  const drag = useRef({
    active: false,
    startX: 0,
    scrollLeft: 0,
  });

  const cursorRef = useRef(null);

  const [index, setIndex] = useState(null);
  const [cursorText, setCursorText] = useState("VIEW");

  /* -----------------------------
     SCROLL INERCIA
  ------------------------------*/

  const animateScroll = useCallback(() => {
    const el = trackRef.current;

    el.scrollLeft += velocity.current;

    velocity.current *= 0.92;

    if (Math.abs(velocity.current) > 0.1) {
      raf.current = requestAnimationFrame(animateScroll);
    } else {
      raf.current = null;
    }
  }, []);

  useEffect(() => {
    const el = trackRef.current;

    const handleWheel = (e) => {
      e.preventDefault();

      velocity.current += e.deltaY * 1.2;

      if (!raf.current) {
        raf.current = requestAnimationFrame(animateScroll);
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", handleWheel);
      cancelAnimationFrame(raf.current);
    };
  }, [animateScroll]);

  /* -----------------------------
     DRAG SCROLL
  ------------------------------*/

  useEffect(() => {
    const slider = trackRef.current;

    const down = (e) => {
      drag.current.active = true;

      drag.current.startX = e.pageX - slider.offsetLeft;
      drag.current.scrollLeft = slider.scrollLeft;

      slider.classList.add("dragging");

      setCursorText("DRAG");
    };

    const up = () => {
      drag.current.active = false;

      slider.classList.remove("dragging");

      setCursorText("VIEW");
    };

    const move = (e) => {
      if (!drag.current.active) return;

      const x = e.pageX - slider.offsetLeft;
      const walk = (x - drag.current.startX) * 1.8;

      slider.scrollLeft = drag.current.scrollLeft - walk;
    };

    slider.addEventListener("mousedown", down);
    slider.addEventListener("mouseup", up);
    slider.addEventListener("mouseleave", up);
    slider.addEventListener("mousemove", move);

    return () => {
      slider.removeEventListener("mousedown", down);
      slider.removeEventListener("mouseup", up);
      slider.removeEventListener("mouseleave", up);
      slider.removeEventListener("mousemove", move);
    };
  }, []);

  /* -----------------------------
     CURSOR (SIN RE-RENDERS)
  ------------------------------*/

  const moveCursor = (e) => {
    const cursor = cursorRef.current;

    if (!cursor) return;

    cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;

    cursor.style.opacity = 1;
  };

  const hideCursor = () => {
    const cursor = cursorRef.current;

    if (cursor) cursor.style.opacity = 0;
  };

  /* -----------------------------
     BOTONES SCROLL
  ------------------------------*/

  const scroll = (dir) => {
    const el = trackRef.current;

    const amount = 500;

    el.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <section className="gallery" id="album">
      <h2 className="aurora-main-title">Album Design</h2>

      <div className="gallery-wrapper">
        <button className="gallery-arrow left" onClick={() => scroll("left")}>
          ←
        </button>

        <div
          className="gallery-track"
          ref={trackRef}
          onMouseMove={moveCursor}
          onMouseLeave={hideCursor}
        >
          {images.map((img, i) => (
            <motion.div
              key={i}
              className="gallery-card"
              whileHover={{ scale: 1.05, rotateY: 8 }}
              transition={{ type: "spring", stiffness: 200 }}
              onClick={() => setIndex(i)}
            >
              <img src={img.src} alt={img.title} loading="lazy" />
            </motion.div>
          ))}
        </div>

        <button className="gallery-arrow right" onClick={() => scroll("right")}>
          →
        </button>
      </div>

      {/* CURSOR */}

      <div ref={cursorRef} className="cursor-view">
        {cursorText}
      </div>

      {/* LIGHTBOX */}

      <AnimatePresence>
        {index !== null && (
          <motion.div
            className="lightbox"
            onClick={() => setIndex(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.img
              src={images[index].src}
              initial={{ scale: 0.85 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.85 }}
              transition={{ type: "spring", stiffness: 200 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
